import React, { FC, useId, useMemo } from 'react';
import * as O from 'fp-ts/Option';
import * as A from 'fp-ts/Array';
import { pipe } from 'fp-ts/function';
import { FormatOptionLabelMeta, Props, StylesConfig } from 'react-select';
import CreatableSelect from 'react-select/creatable';
import { isClient } from '@shared/utils/next';
import { DefaultTheme, useTheme } from 'styled-components';
import { useTranslations } from '@shared/modules/translation/hooks';

function getStyles(theme: DefaultTheme): StylesConfig<SelectOption, boolean> {
  return {
    container: base => ({
      ...base,
      width: '100%',

      '&:hover': {
        borderColor: theme.colors.primary['500'],
      },
    }),
    control: (base, { selectProps }) => {
      return {
        ...base,
        borderRadius: 0,
        cursor: 'pointer',
        borderColor: selectProps.error ? theme.colors.warning : theme.colors.primary['500'],

        '&:hover': {
          borderColor: theme.colors.primary['500'],
          boxShadow: `0 0 0 1px ${theme.colors.primary['500']}`,
        },
      };
    },
    indicatorSeparator: base => ({
      ...base,
      display: 'none',
    }),
    dropdownIndicator: base => ({
      ...base,
      color: '#000000',
    }),
    option: (base, { isSelected }) => ({
      ...base,
      backgroundColor: isSelected ? theme.colors.primary['500'] : theme.colors.tertiary['100'],
      color: isSelected ? theme.colors.tertiary['100'] : theme.colors.tertiary['900'],
      cursor: 'pointer',

      '&:hover': {
        backgroundColor: isSelected ? theme.colors.primary['500'] : theme.colors.primary['300'],
      },
    }),
  };
}

function getNoOptionsMessage(
  noOptionsMessage?: (obj: { inputValue: string }) => string | null,
): (obj: { inputValue: string }) => string | null {
  return noOptionsMessage ?? (() => 'Aucune correspondance');
}

export interface SelectOption {
  value: string;
  label: string;
}

interface SelectProps extends Omit<Props, 'value' | 'isMulti'> {
  value?: string | null;
  options: Array<SelectOption>;
  onChange: (value: string | null) => void;
  error?: boolean;
  squared?: boolean;
  formatOptionLabel?:
    | ((option: SelectOption, labelMeta: FormatOptionLabelMeta<SelectOption, boolean>) => React.ReactNode)
    | undefined;
  customStyle?: StylesConfig<SelectOption, boolean>;
}

export const Select: FC<SelectProps> = ({
  value,
  options,
  onChange,
  isMulti,
  noOptionsMessage,
  error,
  formatOptionLabel,
  customStyle,
  ...rest
}) => {
  const inputId = useId();
  const translations = useTranslations();

  const theme = useTheme();

  const handleChange = (option: SelectOption | ReadonlyArray<SelectOption> | null) => {
    const value = pipe(
      O.fromNullable(option),
      O.chain(opt => (Array.isArray(opt) ? O.none : O.fromNullable(opt as SelectOption))),
      O.chainNullableK(opt => opt.value),
      O.toNullable,
    );

    return onChange(value);
  };

  const formatCreateLabel = (val: string) => {
    return `${translations.select.add} ${val}`;
  };

  const selectValue = useMemo(
    () =>
      pipe(
        O.fromNullable(value),
        O.chain(value =>
          pipe(
            options,
            A.findFirst(opt => opt.value === value),
          ),
        ),
        O.toNullable,
      ),
    [value, options],
  );

  return (
    <CreatableSelect
      {...rest}
      value={selectValue}
      options={options}
      styles={{ ...getStyles(theme), ...customStyle }}
      error={error}
      onChange={handleChange}
      inputId={inputId}
      noOptionsMessage={getNoOptionsMessage(noOptionsMessage)}
      menuPortalTarget={isClient() ? document.body : undefined}
      formatOptionLabel={formatOptionLabel}
      formatCreateLabel={formatCreateLabel}
    />
  );
};
